import type { StatistaDataset } from '@/entities/analysis';

import { extractLinks, type ExtractedLink } from '../../../utils/markdownLinks';

/**
 * The id in a Statista figure's URL: `.../statistics/321064/us-households-...` → `321064`.
 *
 * Both paths count. A dataset's `deepLink` is `/forecasts/` whenever the figure is a
 * projection - three of six rows on a typical analysis - and matching only `/statistics/`
 * left those unmatchable whatever the prose cited.
 */
const STATISTIC_ID = /statista\.com\/(?:statistics|forecasts)\/(\d+)/g;

/**
 * Statistic ids cited anywhere in a passage. A passage is whatever a block
 * renders - a paragraph, a list of trends, an object of named fields - so this
 * walks nested values rather than asking every caller to flatten first. Reading
 * order is preserved, which is what makes "first cited wins" meaningful.
 */
const statisticIdsIn = (passage: unknown): string[] => {
  if (typeof passage === 'string') {
    return [...passage.matchAll(STATISTIC_ID)].map((match) => match[1]);
  }
  if (Array.isArray(passage)) return passage.flatMap(statisticIdsIn);
  if (passage && typeof passage === 'object') {
    return Object.values(passage).flatMap(statisticIdsIn);
  }
  return [];
};

export const statisticIdOf = (url?: string | null): string | undefined => statisticIdsIn(url)[0];

/**
 * Markdown links anywhere in a passage, in reading order. A passage is whatever a block
 * renders - a paragraph, a list of trends, an object of named fields - so this walks nested
 * values rather than asking every caller to flatten first.
 */
export const citedStatisticLinks = (passage: unknown): ExtractedLink[] => {
  if (typeof passage === 'string') return extractLinks(passage);
  if (Array.isArray(passage)) return passage.flatMap(citedStatisticLinks);
  if (passage && typeof passage === 'object') {
    return Object.values(passage).flatMap(citedStatisticLinks);
  }
  return [];
};

/**
 * The datasets a passage of the report is citing.
 *
 * The generated prose links its Statista sources inline, so the report itself
 * states which figure a paragraph is discussing rather than leaving us to infer
 * it from the template slot. That inference was the weak point: a slot name
 * records the SEARCH QUERY the pull issued, never what came back, so
 * `B4_category_ad_spend` has answered with pharmaceutical advertising spend for
 * a baked-goods brand, and two analyses needed opposite slot mappings for the
 * same two sub-sections.
 *
 * Matches on the statistic id rather than the whole URL: the slug after it is
 * descriptive and need not be byte-identical in the prose and in `deepLink`.
 *
 * A block is often several passages - four named cards, a list of trends - so every figure
 * it cites gets its chart, in citation order rather than the order the API returned rows in.
 * A figure cited twice in one block still yields one chart.
 *
 * Returns nothing when a passage cites no Statista figure, or cites only ones this analysis
 * did not store, in which case the prose renders by itself.
 */
export const findCitedStatistaDatasets = (
  datasets: StatistaDataset[],
  ...passages: unknown[]
): StatistaDataset[] => {
  const found: StatistaDataset[] = [];

  for (const citedId of passages.flatMap(statisticIdsIn)) {
    const cited = datasets.find((dataset) => statisticIdsIn(dataset.deepLink).includes(citedId));
    if (cited && !found.includes(cited)) found.push(cited);
  }

  return found;
};
